/* eslint-disable prettier/prettier */
import { useContext, useEffect, useState, useMemo } from 'react';
import './index.css';
import { Page, Card, IndexTable, Text, Button, InlineStack, SkeletonPage, SkeletonBodyText, Badge, Checkbox, InlineGrid, Banner, Box, Pagination } from '@shopify/polaris';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import { notificationService } from '#client/services/notificationService.js';

// New: env flag to control polling (default enabled when missing)
const disablePolling = import.meta.env.VITE_DISABLE_POLLING === 'true';
const pollingEnabled = !disablePolling;
const PAGE_SIZE = 25;

export default function NotificationsPage() {
    const { currentUser } = useContext(SessionContext);

    const queryClient = useQueryClient();

    const staffId = currentUser?.userId;
    const [currentPage, setCurrentPage] = useState(1);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['notifications', staffId ?? 'all', currentPage],
        queryFn: () => notificationService.fetchNotifications(currentPage, PAGE_SIZE),
        // When polling is disabled, stop window-focus refetch and make data "fresh"
        staleTime: pollingEnabled ? 10000 : Infinity,
        refetchOnWindowFocus: pollingEnabled,
        refetchInterval: pollingEnabled ? 30000 : false,
        refetchIntervalInBackground: pollingEnabled,
        enabled: true,
    });

    const items = data?.items || [];
    const total = data?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = Math.min(startIndex + items.length, total);

    // selection state (depends on current page items)
    const [selectedIds, setSelectedIds] = useState([]);
    const unreadIds = useMemo(() => items.filter((n) => !n.isRead).map((n) => n.id), [items]);
    const allSelected = selectedIds.length > 0 && unreadIds.length > 0 && unreadIds.every((id) => selectedIds.includes(id));
    const noneSelected = selectedIds.length === 0;
    const someSelected = selectedIds.length > 0 && !allSelected;

    // Clear selection when page changes
    useEffect(() => {
        setSelectedIds([]);
    }, [currentPage]);

    const onQuerySuccess = () => {
        setSelectedIds([]);
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    };

    // Mark Single as Read
    const { mutate: markAsRead, isPending: isMarking } = useMutation({
        mutationFn: (id) => notificationService.markAsRead(id),
        onSuccess: () => {
            onQuerySuccess();
        },
    });

    // Mark Select as Read
    const { mutate: markSelectedAsRead, isPending: isBulkMarking } = useMutation({
        mutationFn: (ids) => notificationService.markBulkAsRead(ids),
        onSuccess: () => {
            onQuerySuccess();
        },
    });

    // Mark All as Read
    const { mutate: markAllAsRead, isPending: isAllMarking } = useMutation({
        mutationFn: () => notificationService.markAllAsRead(),
        onSuccess: () => {
            onQuerySuccess();
        },
    });

    if (isLoading) {
        return (
            <Page title="Notifications">
                <SkeletonPage>
                    <SkeletonBodyText />
                </SkeletonPage>
            </Page>
        );
    }

    if (isError) {
        return (
            <Page title="Notifications">
                <Card sectioned>
                    <Text tone="critical">{error?.message || 'Failed to load notifications'}</Text>
                </Card>
            </Page>
        );
    }

    return (
        <Page title="Notifications">
            {items.length === 0 ? (
                <Card sectioned>
                    <Text>No notifications found</Text>
                </Card>
            ) : (
                <Card>
                    <Box className="actionBar" paddingBlockStart="4" paddingInline="4">
                        <InlineGrid columns={{ sm: 1, md: 2 }} gap="200">
                            <InlineStack gap="200">
                                <Checkbox
                                    label="Select all on page"
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    onChange={(checked) => {
                                        if (checked) {
                                            setSelectedIds((prev) => [...new Set([...prev, ...unreadIds])]);
                                        } else {
                                            setSelectedIds((prev) => prev.filter((id) => !unreadIds.includes(id)));
                                        }
                                    }}
                                    disabled={unreadIds.length === 0}
                                />
                                {!noneSelected && <Banner tone="info" title={`${selectedIds.length} selected`} />}
                            </InlineStack>
                            <InlineStack align="end" gap="200">
                                <Button variant="primary" disabled={noneSelected || isBulkMarking} onClick={() => markSelectedAsRead(selectedIds)}>
                                    Mark selected as read
                                </Button>
                                <Button variant="secondary" disabled={unreadIds.length === 0 || isAllMarking} onClick={() => markAllAsRead()}>
                                    Mark all as read
                                </Button>
                            </InlineStack>
                        </InlineGrid>
                    </Box>
                    <IndexTable selectable={false} itemCount={total} headings={[{ title: 'Message' }, { title: 'Status' }, { title: 'Read at' }, { title: 'Actions', alignment: 'end' }]}>
                        {items.map((n, index) => (
                            <IndexTable.Row id={String(n.id)} key={n.id} position={startIndex + index}>
                                <IndexTable.Cell className="messageCell">
                                    <InlineStack gap="200" blockAlign="center">
                                        <Checkbox
                                            label=""
                                            checked={selectedIds.includes(n.id)}
                                            onChange={(checked) => setSelectedIds((prev) => (checked ? [...new Set([...prev, n.id])] : prev.filter((id) => id !== n.id)))}
                                            disabled={n.isRead}
                                        />
                                        <Box>
                                            <Text variant="bodyMd">{n.message}</Text>
                                            <Text as="div" tone="subdued">
                                                {new Date(n.createdAt).toLocaleString()}
                                            </Text>
                                        </Box>
                                    </InlineStack>
                                </IndexTable.Cell>
                                <IndexTable.Cell>{n.isRead ? <Badge progress="complete">Read</Badge> : <Badge progress="incomplete">Unread</Badge>}</IndexTable.Cell>
                                <IndexTable.Cell>
                                    <Text>{n.isRead && n.readAt ? new Date(n.readAt).toLocaleString() : '-'}</Text>
                                </IndexTable.Cell>
                                <IndexTable.Cell>
                                    <InlineStack align="end">
                                        <Button disabled={n.isRead || isMarking} onClick={() => markAsRead(n.id)}>
                                            Mark as read
                                        </Button>
                                    </InlineStack>
                                </IndexTable.Cell>
                            </IndexTable.Row>
                        ))}
                    </IndexTable>
                    {total > PAGE_SIZE && (
                        <Box padding="400">
                            <Pagination
                                hasPrevious={currentPage > 1}
                                hasNext={currentPage < totalPages}
                                onPrevious={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                label={`${startIndex + 1} - ${endIndex} of ${total}`}
                            />
                        </Box>
                    )}
                </Card>
            )}
        </Page>
    );
}
